import {Module} from 'vuex';
import {RootState} from '@/store';
import PlanEditResultModel, {PlanEditResultCurrentConfig} from '@/models/PlanEditResultModel';
import {
    fetchEquipmentTypesFormCatalogue,
    fetchResult,
    fetchResultConfig,
    fetchResultConfigsDescriptions,
    fetchSetResultConfig,
} from '@/api/journals/plan/edit/result';
import {WarehouseDescription} from '@/models/PlanEditModel';

type PlanEditResultState = {
    loading: boolean;
    result: PlanEditResultModel | null;
    currentConfig: PlanEditResultCurrentConfig | null;
    isEditing: boolean;
    resultConfigsDescriptions: PlanEditResultCurrentConfig[] | null;
    equipmentTypesFromCatalogue: WarehouseDescription[] | null;
};

const initialState = {
    loading: false,
    result: null,
    currentConfig: null,
    isEditing: false,
    resultConfigsDescriptions: null,
    equipmentTypesFromCatalogue: null,
};

const getInitialState = () => initialState;

const PlanEditResultModule: Module<PlanEditResultState, RootState> = {
    namespaced: true,
    state: {
        loading: false,
        result: null,
        currentConfig: null,
        isEditing: false,
        resultConfigsDescriptions: null,
        equipmentTypesFromCatalogue: null,
    },
    getters: {
        shifts: state => state.result?.Shifts,
        equipmentTypes: state => state.result?.EquipmentTypes,
        hasOwnConfig: state => !!state.currentConfig,
    },
    mutations: {
        setResult: (state, data) => {
            state.result = data;
        },
        setLoading: (state, bool) => {
            state.loading = bool;
        },
        setCurrentConfig: (state, config) => {
            state.currentConfig = config;
        },
        setIsEditing: (state, bool) => (state.isEditing = bool),
        setResultConfigsDescriptions: (state, desc) => (state.resultConfigsDescriptions = desc),
        clearState: state => Object.assign(state, getInitialState()),
        setEquipmentTypeFromCatalogue: (state, descriptions) => (state.equipmentTypesFromCatalogue = descriptions),
    },
    actions: {
        getResult: ({commit}, idPlan) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchResult(idPlan);
                    commit('setResult', data);
                    commit('setCurrentConfig', data.CurrentConfig);
                    resolve();
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            });
        },
        getResultConfig: ({commit}, idPlan) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchResultConfig(idPlan);
                    commit('setCurrentConfig', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        setResultConfig: async ({commit}, {idResultConfig, idResult}) => {
            try {
                const {data} = await fetchSetResultConfig(idResult, idResultConfig);
                commit('setCurrentConfig', data);
            } catch (e) {
                throw e;
            }
        },
        getResultConfigsDescriptions: ({commit}) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchResultConfigsDescriptions();
                    commit('setResultConfigsDescriptions', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        getEquipmentTypesFromCatalogue: ({commit}) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchEquipmentTypesFormCatalogue();
                    commit('setEquipmentTypeFromCatalogue', data);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        init: async ({commit, dispatch}, idPlan) => {
            commit('setLoading', true);
            try {
                await Promise.all([
                    dispatch('getResult', idPlan),
                    dispatch('getResultConfigsDescriptions'),
                    dispatch('getEquipmentTypesFromCatalogue'),
                ]);
            } finally {
                commit('setLoading', false);
            }
        },
    },
};

export default PlanEditResultModule;
